import React, { useEffect, useRef, useState } from "react";
import { Loader2, AlertTriangle, ArrowRight } from "lucide-react";
import { useAuth } from "@/lib/AuthContext";

/**
 * AuthCallback — runs when the user returns from Google sign-in via
 * Emergent Auth. URL fragment looks like `#session_id=XYZ`.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS — THIS BREAKS THE AUTH.
 *
 * Failure modes we surface clearly (instead of a generic 'try again'):
 *   • Missing session_id in the URL fragment
 *   • Session_id already consumed (Emergent returns 401 — the user reloaded
 *     the callback page or the back-arrow bounced them through twice)
 *   • Upstream Emergent auth-proxy is down (502)
 *   • Network or CORS error reaching our own backend
 */
export default function AuthCallback() {
  const { completeGoogleSignIn, loginWithGoogle } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || "";
    const match = hash.match(/session_id=([^&]+)/);
    if (!match) {
      setError("Missing sign-in session. Click Continue with Google to try again.");
      return;
    }
    const sessionId = decodeURIComponent(match[1]);

    (async () => {
      try {
        await completeGoogleSignIn(sessionId);
        // Hard-redirect to clear the consumed session_id from the URL fragment so
        // a back/forward navigation can't accidentally retry the (now invalid) flow.
        window.location.replace("/dashboard");
      } catch (e) {
        // Try to extract the most useful diagnostic the user (and we) can see.
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        let msg = detail;
        if (!msg) {
          if (e?.message?.includes("Network")) msg = "Network error reaching the sign-in server. Check your connection and try again.";
          else if (status) msg = `Sign-in failed (HTTP ${status}). Click Continue with Google to restart.`;
          else msg = "Sign-in failed unexpectedly. Click Continue with Google to restart.";
        }
        // eslint-disable-next-line no-console
        console.error("[AuthCallback]", { status, detail, error: e });
        setError(typeof msg === "string" ? msg : JSON.stringify(msg));
      }
    })();
     
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] text-[#F4F4F0] flex items-center justify-center p-6">
      <div className="text-center max-w-md w-full">
        {error ? (
          <div className="border border-red-500/40 bg-red-500/5 p-6">
            <AlertTriangle className="w-8 h-8 text-red-300 mx-auto mb-3" />
            <p className="text-red-200 font-display text-lg font-bold mb-2">Google sign-in didn't complete</p>
            <p className="text-sm text-[#A0A0A0] mb-5 leading-relaxed" data-testid="google-auth-error">{error}</p>
            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <button
                onClick={loginWithGoogle}
                data-testid="google-retry-button"
                className="inline-flex items-center justify-center gap-2 bg-[#D4AF37] text-[#080808] font-bold px-5 py-2.5 text-sm hover:bg-[#F0C84B]"
              >
                Continue with Google <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href="/login"
                data-testid="google-back-to-login"
                className="inline-flex items-center justify-center px-5 py-2.5 text-sm border border-[#2A2A2A] hover:border-[#D4AF37] text-[#F4F4F0]"
              >
                Use email & password
              </a>
            </div>
            <p className="text-[10px] uppercase tracking-wider text-[#5A5A5A] mt-5">
              If this keeps happening, try a fresh incognito window with ad-blockers off.
            </p>
          </div>
        ) : (
          <>
            <Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin mx-auto mb-4" />
            <p className="text-sm text-[#A0A0A0]">Finishing Google sign-in…</p>
          </>
        )}
      </div>
    </div>
  );
}
